<template>
  <section class="container">
    <h2 class="mb-5">{{ page.title }}</h2>

    <b-form @submit.stop.prevent>
      <b-form-group label="Titre de la délibération*">
        <b-form-input
          id="input-title"
          name="input-title"
          @change="setValue('title', $event)"
          :value="form.title"
          :state="validateState('title')"
          aria-describedby="input-title"
          type="text"
        ></b-form-input>
        <b-form-invalid-feedback id="input-title">
          Ce champ est requis.
        </b-form-invalid-feedback>
        <b-form-text class="text-right">140 caractères maximum </b-form-text>
      </b-form-group>

      <b-form-group label="Description">
        <b-form-textarea
          id="input-description"
          name="input-description"
          @change="setValue('description', $event)"
          :value="form.description"
          :state="validateState('description')"
          aria-describedby="input-description"
          type="text"
        ></b-form-textarea>

        <b-form-invalid-feedback id="input-description">
          Ce champ est requis.
        </b-form-invalid-feedback>
        <b-form-text class="text-right">250 caractères maximum </b-form-text>
      </b-form-group>

      <b-form-group label="Groupe*">
        <b-form-input
          id="input-group"
          name="input-group"
          v-model="groupSearchQuery"
          type="text"
          :formatter="formatter"
          @change="setValue('group', $event)"
          :value="form.group"
          :state="validateState('group')"
          autocorrect="off"
          autocomplete="off"
          autocapitalize="off"
        ></b-form-input>

        <b-form-select
          v-if="groups.length"
          v-model="form.group"
          :options="groups"
          text-field="name"
          value-field="name"
          :select-size="groups.length + 1"
          @input="onGroupSelect"
          autocorrect="off"
          autocomplete="off"
          autocapitalize="off"
        ></b-form-select>

        <b-form-invalid-feedback id="input-group">
          Ce champ est requis.
        </b-form-invalid-feedback>
        <b-form-text>
          Choisissez un nom de groupe unique, comme « lundi matin » ou «
          #0009876 ». Un groupe rassemble les participants qui doivent délibérer
          ensemble sous votre supervision.
        </b-form-text>
      </b-form-group>

      <b-form-group label="URL*">
        <b-form-text>{{ urlPreview }}</b-form-text>
        <b-form-input
          id="input-url"
          name="input-url"
          @change="setValue('url', $event)"
          :value="form.url"
          :state="validateState('url')"
          aria-describedby="input-url"
          type="text"
          :formatter="formatter"
        ></b-form-input>
        <b-tooltip target="input-url" placement="left">
          Le champ URL est une partie obligatoire qui doit être unique pour
          chacune des délibérations pour des raisons de sécurité.
        </b-tooltip>
        <b-form-invalid-feedback id="input-url">
          Ce champ est requis et doit être unique.
        </b-form-invalid-feedback>
        <b-form-text>
          L'URL ne doit contenir ni d'espaces, ni d'accents, ni de caractères
          spéciaux. Faites- le court et unique.
        </b-form-text>
      </b-form-group>

      <label for="activation">Période active de délibération</label>
      <small class="form-text text-muted">
        La délibération deviendra publique durant cette période et les
        participants pourront intervenir.
      </small>

      <b-form-group
        label-cols="12"
        label-cols-md="3"
        label="Date et heure de début*"
      >
        <b-form-row>
          <b-col sm="12" md="6">
            <b-form-datepicker
              id="start_date"
              v-model="form.start_date"
              class="mb-1"
              locale="fr-CA"
              placeholder="Choisissez la date"
              label-no-date-selected="Choisissez la date"
              label-close-button="Fermer"
              label-help=""
              @input="setValue('start_date', $event)"
              :state="validateState('start_date')"
            ></b-form-datepicker
          ></b-col>
          <b-col sm="12" md="6">
            <b-form-timepicker
              id="start_time"
              v-model="form.start_time"
              locale="fr-CA"
              placeholder="Choisissez l'heure"
              label-no-time-selected="Choisissez l'heure"
              label-close-button="Fermer"
              @input="setValue('start_time', $event)"
            ></b-form-timepicker>
          </b-col>
        </b-form-row>
      </b-form-group>

      <b-form-group
        label-cols="12"
        label-cols-md="3"
        label="Date et heure de fin*"
      >
        <b-form-row>
          <b-col sm="12" md="6">
            <b-form-datepicker
              id="end_date"
              v-model="form.end_date"
              class="mb-1"
              locale="fr-CA"
              placeholder="Choisissez la date"
              label-no-date-selected="Choisissez la date"
              label-close-button="Fermer"
              label-help=""
              :state="validateState('end_date')"
            ></b-form-datepicker
          ></b-col>
          <b-col sm="12" md="6">
            <b-form-timepicker
              id="end_time"
              v-model="form.end_time"
              locale="fr-CA"
              placeholder="Choisissez l'heure"
              label-no-time-selected="Choisissez l'heure"
              label-close-button="Fermer"
            ></b-form-timepicker>
          </b-col>
        </b-form-row>
      </b-form-group>

      <b-form-group label="Visibilité">
        <b-form-text>
          Quand la délibération sera en ligne, vous pourrez soit la rendre
          consultable pour tous ou pour vous seulement. Sachez que vous pouvez
          changer sa visibilité n'importe quand.
        </b-form-text>
        <b-form-radio-group
          id="radio-group-2"
          v-model="form.privacy"
          name="radio-sub-component"
        >
          <b-form-row>
            <b-col sm="12" md="4">
              <b-form-radio class="my-2" :value="1">
                Privée - La délibération ne peut être consultée que par son
                créateur une fois en ligne.
              </b-form-radio>
            </b-col>
            <b-col sm="12" md="4">
              <b-form-radio class="my-2" :value="2">
                Publique aux étudiants - la délibération est accessible à tous les étudiants via l'URL.
              </b-form-radio>
            </b-col>
            <b-col sm="12" md="4">
              <b-form-radio class="my-2" :value="3"
                >Publique aux étudiants et aux invités: Donner la permission à des invités d'accéder et participer à la délibération.
              </b-form-radio>
            </b-col>
          </b-form-row>
        </b-form-radio-group>
      </b-form-group>

      <b-btn pill v-b-toggle.advancedOptions variant="outline-info"
        >Paramètres avancés</b-btn
      >
      <b-collapse id="advancedOptions" class="mt-2">
        <section class="container border rounded">
          <p class="mt-3">Remplacer les marqueurs de relation par défaut.</p>
          <b-row class="mb-4">
            <b-col>
              <b-form-group
                id="positive"
                label="Parce que"
                label-for="positive"
              >
                <b-form-input
                  v-model="advanced.positive"
                  id="positive"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                id="negative"
                label="Par contre"
                label-for="negative"
              >
                <b-form-input
                  v-model="advanced.negative"
                  id="negative"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group id="neutral" label="Donc" label-for="neutral">
                <b-form-input
                  v-model="advanced.neutral"
                  id="neutral"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mb-4">
            <b-col>
              <div>
                Suggestions automatiques aux participants durant la délibération
              </div>
              <small>
                Vous pouvez activer ou désactiver les suggestions automatiques.
                Elles sont activées par défaut. Les suggestions sont les
                suivantes :
              </small>
              <b-table
                class="my-3"
                stacked="md"
                :fields="fields"
                :items="configRules"
              >
                <template v-slot:cell(activation)="row">
                  <b-form-checkbox
                    v-model="
                      configuration.find(c => c.id == row.item.id).status
                    "
                    switch
                  ></b-form-checkbox>
                </template>
              </b-table>
            </b-col>
          </b-row>
        </section>
      </b-collapse>

      <b-btn
        @click="page.btnAction"
        :disabled="callStatus"
        type="submit"
        class="float-right mt-5 px-4 ml-2"
        pill
        variant="primary"
        ><span>{{ page.btnText }}</span>
        <b-spinner class="ml-2" small v-if="callStatus"></b-spinner>
      </b-btn>
    </b-form>
  </section>
</template>

<script>
/**
 * @file       Deliberation detail component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import store from "@/store";
import { mapState } from "vuex";
import { debounce } from "lodash";
import { required, numeric, maxLength } from "vuelidate/lib/validators";
import moment from "moment";

var isOriginalStartDate;
const startDate = (value, vm) => {
  if (isOriginalStartDate) {
    return true;
  }
  let startTime = vm.start_time ? vm.start_time.split(":") : null;
  if (startTime) {
    let startDate = moment(vm.start_date).set({
      hour: startTime[0],
      minute: startTime[1]
    });
    return startDate.add(10, "minutes").valueOf() >= moment().valueOf();
  } else {
    return false;
  }
};

const endDate = (value, vm) => {
  let endTime = vm.end_time ? vm.end_time.split(":") : null;
  if (endTime) {
    let endDate = moment(vm.end_date).set({
      hour: endTime[0],
      minute: endTime[1]
    });
    let startTime = vm?.start_time.split(":");
    let startDate = moment(vm.start_date).set({
      hour: startTime[0],
      minute: startTime[1]
    });
    return (
      endDate.valueOf() > startDate.valueOf() &&
      endDate.valueOf() > moment().valueOf()
    );
  } else {
    return false;
  }
};

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "detailDeliberation",

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      form: {
        title: null,
        description: null,
        url: "",
        start_date: null,
        start_time: null,
        end_date: null,
        end_time: null,
        status: 1,
        privacy: 2,
        group: ""
      },
      fields: [
        {
          key: "activation",
          label: "Activation",
          class: "text-center"
        },
        {
          key: "objective",
          label: "But"
        },
        {
          key: "condition",
          label: "Condition"
        },
        {
          key: "preview",
          label: "Prévisualisation"
        }
      ],
      configuration: [
        { id: 1, status: true },
        { id: 2, status: true },
        { id: 3, status: true },
        { id: 4, status: true },
        { id: 5, status: true },
        { id: 6, status: true }
      ],
      advanced: {
        positive: "Parce que",
        negative: "Par contre",
        neutral: "Donc"
      },
      callStatus: false,
      groups: []
    };
  },

  /**
   * Vuelidate form validations.
   * 
   * @type  {Object}
   */
  validations: {
    form: {
      title: { required, maxLength: maxLength(140) },
      description: { maxLength: maxLength(250) },
      url: { required },
      start_date: { required, startDate },
      start_time: {},
      end_date: { required, endDate },
      end_time: {},
      status: { required, numeric },
      privacy: {},
      group: { required }
    }
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      dlibr: state => state.deliberations.teacherDlibr.data.data.find(d => d),
      config: state => state.teachers.config.data.data,
      profile: state =>
        state.auth.profile.data ? state.auth.profile.data.data.find(u => u) : {}
    }),

    /**
     * Config rules.
     *
     * @return    {Object}     The config rules.
     */
    configRules() {
      const rules = [];
      this.dlibr
        ? this.dlibr.deliberation_has_config_rule.forEach(e => {
            rules.push({
              ...e.config_rule.find(c => c),
              enabled: Boolean(e.status)
            });
          })
        : null;

      return rules.length ? rules : this.config;
    },

    /**
     * Group search query.
     */
    groupSearchQuery: {
      get() {
        return this.form.group;
      },
      set: debounce(function(value) {
        if (value) {
          this.form.group = value;
          this.$store.commit("groups/setTeacherGroupsFilters", {
            reset: true,
            fields: {
              teacher: this.profile.id,
              resourcesPerPage: 10,
              name: value,
              status: 1
            }
          });
          this.$store.dispatch("groups/getTeacherGroups").then(({ data }) => {
            this.groups = data.data;
          });
        } else {
          this.groups = [];
          this.form.group = "";
        }
      }, 175)
    },

    /**
     * Format payload.
     *
     * @return     {Object}  The payload.
     */
    formatPayload() {
      const payload = {
        deliberation: {
          title: this.form.title,
          description: this.form.description,
          url: this.urlToPut,
          start_date: this.dateTime(this.form.start_date, this.form.start_time),
          end_date: this.dateTime(this.form.end_date, this.form.end_time),
          status: this.statusFromDates(),
          privacy: this.form.privacy,
          group: this.form.group,
          positive: this.advanced.positive,
          negative: this.advanced.negative,
          neutral: this.advanced.neutral
        },
        configuration: {
          ...this.configuration
        }
      };
      this.isDetail ? (payload.deliberation.id = this.$route.params.id) : null;

      return payload;
    },

    /**
     * Check if detail route.
     *
     * @return     {bool}  true or false.
     */
    isDetail() {
      return Boolean(this.$route.params.id);
    },

    /**
     * Get preview URL.
     *
     * @return     {String}  The preview URL.
     */
    urlPreview() {
      return this.form.url.startsWith(this.form.group)
        ? `${window.location.origin}/#/${this.profile.id}/${this.form.url}`
        : `${window.location.origin}/#/${this.profile.id}/${this.form.group}-${this.form.url}`;
    },

    /**
     * Get URL to put.
     *
     * @return     {String}  The URL.
     */
    urlToPut() {
      return this.form.url.startsWith(this.form.group)
        ? this.form.url
        : `${this.form.group}-${this.form.url}`;
    },

    /**
     * Page button action.
     *
     * @return     {Object}   The details.
     */
    page() {
      return {
        title: this.isDetail
          ? "Éditer la délibération"
          : "Nouvelle délibération",
        btnText: this.isDetail
          ? "Sauvegarder la modification"
          : "Créer la délibération",
        btnAction: this.isDetail ? this.onUpdate : this.onCreate
      };
    }
  },

  /**
   * Before route change.
   * 
   * @param  {string}   to     Destination
   * @param  {string}   from   (Unused)
   * @param  {Object}   next   The action
   */
  beforeRouteEnter(to, from, next) {
    if (to.params.id) {
      store.commit("deliberations/setTeacherDlibrFilters", {
        fields: { id: to.params.id },
        reset: true
      });
      store.dispatch("deliberations/getTeacherDlibr").then(() => next());
    } else {
      store.dispatch("teachers/getConfig").then(() => next());
    }
  },
  mounted() {
    if (this.isDetail) {
      isOriginalStartDate = true;
      this.form.title = this.dlibr.title;
      this.form.description = this.dlibr.description;
      this.form.url = this.dlibr.url;
      this.form.start_date = this.dlibr.start_date.split(" ")[0];
      this.form.start_time = this.dlibr.start_date.split(" ").slice(-1)[0];
      this.form.end_date = this.dlibr.end_date.split(" ")[0];
      this.form.end_time = this.dlibr.end_date.split(" ").slice(-1)[0];
      this.form.privacy = this.dlibr.privacy;
      this.form.status = this.dlibr.status;
      this.form.group = this.dlibr.group.name;
      this.advanced.negative = this.dlibr.negative;
      this.advanced.neutral = this.dlibr.neutral;
      this.advanced.positive = this.dlibr.positive;

      this.configRules.forEach(r => {
        const found = this.configuration.find(c => c.id == r.id);
        found ? (found.status = r.enabled) : null;
      });
    } else {
      let today = new Date();
      this.form.start_date = moment()
        .format()
        .split("T")[0];
      this.form.start_time = `${today.getHours()}:${today.getMinutes()}`;
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Sets the form value.
     *
     * @param      {string}  key     The new value
     * @param      {string}  value   The value
     */
    setValue(key, value) {
      if (key === "start_date" || key === "start_time") {
        isOriginalStartDate = false;
      }
      this.form[key] = value;
      this.$v.form[key].$touch();
    },

    /**
     * Validate the form state.
     *
     * @param      {string}  name    The input name
     *
     * @return     {bool}  Dirty or not.
     */
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    /**
     * Create event.
     */
    onCreate() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      this.callStatus = true;
      this.$store
        .dispatch("deliberations/postDlibr", this.formatPayload)
        .then(() => {
          this.callStatus = false;
          this.$root.$bvToast.toast(`La délibération à été créé avec succès.`, {
            title: "Succès"
          });
          this.$router.push({
            name: "teacher.own.deliberations",
            params: { id: this.profile.id }
          });
        })
        .catch(() => {
          this.callStatus = false;
          this.$root.$bvToast.toast(
            `La délibération n'à pas été créé avec succès.`,
            {
              title: "Erreur",
              variant: "danger"
            }
          );
        });
    },

    /**
     * Update event.
     */
    onUpdate() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      this.callStatus = true;
      this.$store
        .dispatch("deliberations/putDlibr", this.formatPayload)
        .then(() => {
          this.callStatus = false;
          this.$root.$bvToast.toast(
            `La délibération à été mise à jour avec succès.`,
            {
              title: "Succès"
            }
          );
          this.$router.push({
            name: "teacher.own.deliberations",
            params: { id: this.profile.id }
          });
        })
        .catch(() => {
          this.callStatus = false;
          this.$root.$bvToast.toast(
            `La délibération n'à pas été créé mise à jour avec succès.`,
            {
              title: "Erreur",
              variant: "danger"
            }
          );
        });
    },

    /**
     * Date time formatter.
     *
     * @param     {String}   dateStr   The date
     * @param     {String}   timeStr   The time
     *
     * @return    {String}   Formatted date time
     */
    dateTime(dateStr, timeStr) {
      return `${dateStr} ${timeStr ? timeStr : "00:00:00"}`;
    },

    /**
     * Group select.
     *
     * @param     {Object}   group   The group
     */
    onGroupSelect(group) {
      const found = this.groups.find(g => g.name == group);
      this.form.group = found ? found.name : group;
      this.groups = [];
    },

    /**
     * String formatter.
     *
     * @param     {String}   value   The value
     *
     * @return    {String}   The formatted value
     */
    formatter(value) {
      return value
        .toLowerCase()
        .replace(/ +/g, "-")
        .replace(/--+/g, "-")
        .replace(/[^\w\s\][^-]/gi, "");
    },

    /**
     * Status from dates.
     *
     * @return    {int}   0 or 1
     */
    statusFromDates() {
      return moment(this.form.start_date).isAfter(moment().format()) ||
        moment(this.form.end_date).isBefore(moment().format())
        ? 0
        : 1;
    }
  }
};
</script>
